const adminModelProvider = {
  actions: {
    add: 'Add model',
    delete: 'Delete',
    edit: 'Edit',
    open: 'Open actions for {{name}}',
    probe: 'Run liveness check',
    probeFailed: 'Liveness check failed',
    probeSlow: 'Liveness check completed with high latency',
    probeSuccess: 'Liveness check completed',
  },
  columnSelector: {
    label: 'Choose model registry columns',
    trigger: 'Columns',
  },
  deleteDialog: {
    cancel: 'Cancel',
    close: 'Close delete confirmation',
    confirm: 'Delete',
    failed: 'Failed to delete model',
    message:
      'Delete model {{name}}? Distribution keys and fallback settings that reference this model can no longer use it.',
    subtitle:
      'This calls the backend delete endpoint. Saved model connection details are not retained.',
    success: 'Model deleted',
    title: 'Delete model?',
  },
  description:
    'Manage model providers, interfaces, aliases, connection settings, pricing, and status.',
  drawer: {
    cancel: 'Cancel',
    configFields: {
      anthropic_version: 'Anthropic Version',
      api_key: 'API Key',
      organization: 'Organization',
      project: 'Project',
    },
    configFieldHelp: {
      anthropic_version: 'Anthropic API version header. The default value is usually enough.',
      api_key:
        'Credential used to call the upstream model provider API. It is saved as a sensitive field.',
      organization:
        'Optional OpenAI Organization header. Fill it only when the provider requires it.',
      project: 'Optional OpenAI Project header. Fill it only when the provider requires it.',
    },
    createTitle: 'Add model',
    description: 'Update model connection details, pricing, and status.',
    detailLoadFailed:
      'Try again later, or check whether this admin still has model gateway access.',
    detailLoadFailedTitle: 'Failed to load model detail',
    editTabs: {
      config: 'Model config',
      fallback: 'Fallback model',
      label: 'Model edit tabs',
      probe: 'Model probe',
      pricing: 'Model pricing',
    },
    editTitle: 'Edit / {{name}}',
    feedback: {
      createSuccess: 'Model created',
      fallbackSaveFailed:
        'The model was saved, but the fallback settings failed to save. Check the fallback settings again.',
      partialSaveFailed: 'The model was saved, but some linked settings failed to save.',
      probeSaveFailed:
        'The model was saved, but the probe settings failed to save. Check the model probe settings again.',
      submitFailed: 'Save failed',
      updateSuccess: 'Model saved',
      validationFailed: 'Fix the form first',
    },
    fields: {
      baseUrl: 'Base URL',
      enabled: 'Model enabled',
      inputPricePerMillion: 'Input price / 1M tokens',
      interfaceType: 'Interface protocol',
      modelName: 'Model name',
      outputPricePerMillion: 'Output price / 1M tokens',
      pricingCurrency: 'Pricing currency',
      provider: 'Provider',
      publicModelName: 'Display name',
      status: 'Status',
    },
    fieldHelp: {
      baseUrl:
        'API root URL of the upstream model service. It must start with http:// or https://.',
      enabled: 'When disabled, this model will not be used as a callable routing target.',
      interfaceType:
        'Interface protocol the model gateway uses to adapt request format and credential fields.',
      modelName: 'Real model ID sent to the upstream provider, for example gpt-5-mini.',
      provider: 'Model provider. Changing it resets the interface config to the provider default.',
      publicModelName:
        'Model identifier shown to users, apps, and distribution keys. It can be an internal alias.',
    },
    fallback: {
      activeHint:
        'Requests are currently routed to fallback model {{model}} until the primary probe succeeds again.',
      disabledState: 'Disabled',
      enabled: 'Enable fallback',
      enabledHelp:
        'When the primary model is unavailable, route requests to another enabled model with the same interface.',
      enabledState: 'Enabled',
      targetHelp: 'The fallback model must be enabled and use the same interface as this model.',
      targetLabel: 'Fallback model',
      targetPlaceholder: 'Select a fallback model',
      triggerHelp:
        'Choose whether fallback is triggered by probe failures, runtime call failures, or both.',
      triggerLabel: 'Trigger mode',
      triggerModes: {
        probe_only: 'Switch only on probe failure',
        probe_or_runtime: 'Switch on probe or runtime failure',
        runtime_only: 'Switch only on runtime failure',
      },
    },
    hideSecret: 'Hide secret',
    placeholders: {
      baseUrl: 'For example: https://api.openai.com/v1',
      modelName: 'For example: gpt-5-mini',
      publicModelName: 'For example: GPT-5 Mini',
      secret: 'Leave blank to keep the saved secret',
      secretWithSaved: 'Saved: {{value}}. Leave blank to keep it.',
    },
    probe: {
      disabledState: 'Disabled',
      enabled: 'Enable model probe',
      enabledHelp:
        'When enabled, the model gateway actively calls this model on a schedule and records liveness and latency.',
      enabledState: 'Enabled',
      intervalHelp: 'Interval between automatic probes in normal mode. Range: 5-1440 minutes.',
      intervalLabel: 'Probe interval (minutes)',
      mechanismDescription:
        'The system sends a minimal request to verify upstream availability and records total time, first-token time, and throughput. After a failure it enters recovery mode with a shorter retry interval. A successful probe restores the normal interval and can recover fallback routing.',
      mechanismTitle: 'Probe mechanism',
      nextProbeAt: 'Next scheduled probe: {{time}}',
      recentTitle: 'Recent probe information',
      recoveryIntervalHelp:
        'Probe interval while failed or in recovery mode. Range: 1-1440 minutes.',
      recoveryIntervalLabel: 'Recovery probe interval (minutes)',
      warningLatencyHelp: 'Responses slower than this threshold are marked slow, not failed.',
      warningLatencyLabel: 'Latency warning threshold (ms)',
    },
    save: 'Save',
    sections: {
      connection: 'Model and connection',
      connectionDescription:
        'The model name is sent upstream. The public name is used for distribution and app selection.',
      credentials: '{{interface}} config',
      policy: 'Pricing and status',
      policyDescription:
        'Pricing is an optional override. Leave it blank to use model gateway defaults.',
      provider: 'Provider and interface',
      providerDescription: 'The interface controls required credentials and the default Base URL.',
    },
    showSecret: 'Show secret',
    submitFailed: 'Failed to save the model. Try again later.',
    validation: {
      baseUrlInvalid: 'Base URL must start with http:// or https://',
      baseUrlRequired: 'Enter a Base URL',
      fieldInvalid: '{{field}} format is invalid',
      fieldRequired: '{{field}} is required',
      fallbackTargetRequired: 'Select a fallback model when fallback is enabled',
      interfaceRequired: 'Select an interface protocol',
      modelNameRequired: 'Enter the model name',
      probeIntervalInvalid: 'Probe interval must be an integer from 5 to 1440 minutes',
      probeLatencyInvalid: 'Latency warning threshold must be an integer from 100 to 120000 ms',
      probeRecoveryInvalid: 'Recovery probe interval must be an integer from 1 to 1440 minutes',
      pricingRequired: 'Pricing requires currency, input price, and output price',
      providerRequired: 'Select a provider',
      publicModelNameRequired: 'Enter the display name',
    },
    wizard: {
      create: 'Create model',
      headerDescription: 'Step {{current}} / {{total}}',
      next: 'Next',
      previous: 'Previous',
      progressLabel: 'Add model progress: step {{current}} of {{total}}, currently {{step}}',
      provider: {
        defaultDescription: 'Select a provider before configuring the interface protocol',
        descriptions: {
          anthropic: 'Claude model series.',
          deepseek: 'DeepSeek model series.',
          gemini: 'Gemini model series.',
          glm: 'GLM model series.',
          kimi: 'Kimi model series.',
          minimax: 'MiniMax model series.',
          openai: 'GPT model series.',
          qwen: 'Qwen model series.',
          xai: 'Grok model series.',
        },
      },
      steps: {
        config: {
          title: 'Model config',
        },
        fallback: {
          title: 'Fallback model',
        },
        probe: {
          title: 'Model probe',
        },
        pricing: {
          title: 'Pricing config',
        },
        provider: {
          title: 'Choose provider',
        },
      },
    },
  },
  empty: 'No registered models yet',
  errorDescription: 'Try again later, or check whether this admin still has access.',
  errorDescriptionWithDetail: 'Backend response: {{detail}}',
  errorTitle: 'Failed to load models',
  filters: {
    allProviders: 'All providers',
    allStatuses: 'All statuses',
    providerLabel: 'Filter model provider',
    searchLabel: 'Search models',
    searchPlaceholder: 'Search model, provider, or interface protocol',
    statusLabel: 'Filter model status',
  },
  pagination: {
    next: 'Next',
    pageSize: '{{count}} / page',
    previous: 'Previous',
  },
  probe: {
    checkedAt: 'Checked at',
    detailsLabel: 'View last 24 hours liveness details',
    detailsTitle: 'Last 24 hours liveness',
    metrics: {
      latency: 'Total time',
      tps: 'TPS',
      ttft: 'First token',
    },
    noData: 'No probe',
    statusLabel: 'Status',
    status: {
      failed: 'Failed',
      healthy: 'Alive',
      slow: 'Slow',
      unknown: 'No probe',
    },
  },
  retry: 'Retry',
  status: {
    active: 'Enabled',
    disabled: 'Disabled',
  },
  table: {
    actions: 'Actions',
    actualModelName: 'Model name',
    createdAt: 'Created at',
    interface: 'Interface protocol',
    noPricing: '-',
    pricing: 'Pricing',
    probe: 'Liveness',
    provider: 'Model provider',
    publicModelName: 'Display name',
    status: 'Status',
  },
  title: 'Model Registry',
  workspaceLabel: 'Model registry workspace',
}

export default adminModelProvider
